import {DataStatus} from 'platform/components';
import {Box, Space, Stack, Text, useDevice, VStack, getValueByDevice} from 'platform/foundation';

import {useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerResponseBodyV2, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {OwnershipType} from '../../CustomerVehicleSelection/types/ownershipType';
import {OwnershipCustomerCard} from './OwnershipCustomerCard';

interface OwnershipProps {
  vehicleId: string;
  resourceId: EntityResourceIds;
  recordId: string;
  onOwnershipChange: (customerType: OwnershipType) => void;
  onDelete: (customerType: OwnershipType) => void;
  onCopy: (customer: CustomerResponseBodyV2) => void;
  onMove: (excludedOwnership: OwnershipType) => (customer: CustomerResponseBodyV2) => void;
}

export function Ownership(props: OwnershipProps) {
  const {
    data: vehicleCustomers,
    isSuccess: isVehicleCustomerSuccess,
    isLoading: isVehicleCustomerLoading,
    isError: isVehicleCustomerError,
  } = useGetVehicleCustomerQuery({vehicleId: props.vehicleId}, {skip: !props.vehicleId});
  const device = useDevice();
  const [isOwnerVisible, setIsOwnerVisible] = useState(false);
  const [isManagerVisible, setIsManagerVisible] = useState(false);
  const isFirstAssignment =
    isVehicleCustomerSuccess &&
    !vehicleCustomers?.some(
      (assignment) =>
        isNotNilOrEmpty(assignment?.customerType) &&
        assignment?.customerType !== 'CORVEHICLECUSTOMERTYPE_INVOICE' &&
        isNotNilOrEmpty(assignment?.customerId)
    );

  const operator = (
    <OwnershipCustomerCard
      vehicleId={props.vehicleId}
      resourceId={props.resourceId}
      recordId={props.recordId}
      ownershipType="CORVEHICLECUSTOMERTYPE_OPERATOR"
      onChange={() => props.onOwnershipChange('CORVEHICLECUSTOMERTYPE_OPERATOR')}
      onCopy={props.onCopy}
      colorStripe="blue"
      isFirstAssignment={isFirstAssignment}
      data-testid={testIds.workshop.serviceCaseDetail('ownership-operator')}
    />
  );
  const user = (
    <OwnershipCustomerCard
      vehicleId={props.vehicleId}
      resourceId={props.resourceId}
      recordId={props.recordId}
      ownershipType="CORVEHICLECUSTOMERTYPE_USER"
      isAssignmentDisabled={isFirstAssignment}
      onDelete={() => props.onDelete('CORVEHICLECUSTOMERTYPE_USER')}
      onChange={() => props.onOwnershipChange('CORVEHICLECUSTOMERTYPE_USER')}
      onMove={props.onMove('CORVEHICLECUSTOMERTYPE_USER')}
      data-testid={testIds.workshop.serviceCaseDetail('ownership-user')}
    />
  );
  const owner = (
    <OwnershipCustomerCard
      vehicleId={props.vehicleId}
      resourceId={props.resourceId}
      recordId={props.recordId}
      ownershipType="CORVEHICLECUSTOMERTYPE_OWNER"
      isAssignmentDisabled={isFirstAssignment}
      onDelete={() => props.onDelete('CORVEHICLECUSTOMERTYPE_OWNER')}
      onChange={() => props.onOwnershipChange('CORVEHICLECUSTOMERTYPE_OWNER')}
      onMove={props.onMove('CORVEHICLECUSTOMERTYPE_OWNER')}
      visibility={{isVisible: isOwnerVisible, onChange: setIsOwnerVisible}}
      data-testid={testIds.workshop.serviceCaseDetail('ownership-owner')}
    />
  );
  const manager = (
    <OwnershipCustomerCard
      vehicleId={props.vehicleId}
      resourceId={props.resourceId}
      recordId={props.recordId}
      ownershipType="CORVEHICLECUSTOMERTYPE_MANAGER"
      isAssignmentDisabled={isFirstAssignment}
      onDelete={() => props.onDelete('CORVEHICLECUSTOMERTYPE_MANAGER')}
      onChange={() => props.onOwnershipChange('CORVEHICLECUSTOMERTYPE_MANAGER')}
      onMove={props.onMove('CORVEHICLECUSTOMERTYPE_MANAGER')}
      visibility={{isVisible: isManagerVisible, onChange: setIsManagerVisible}}
      data-testid={testIds.workshop.serviceCaseDetail('ownership-manager')}
    />
  );

  return (
    <>
      <Text color="secondary" size="small">
        {i18n.t('entity.ownership.label.ownershipInfo')}
      </Text>
      <Space vertical={4} />
      <DataStatus
        isLoading={isVehicleCustomerLoading}
        isError={isVehicleCustomerError}
        minHeight={100}
      >
        {getValueByDevice(device, false, false, false, true) ? (
          <Stack direction="row" spacing={4}>
            <Box flex={1} minWidth={0}>
              <VStack spacing={4}>
                {operator}
                {owner}
              </VStack>
            </Box>
            <Box flex={1} minWidth={0}>
              <VStack spacing={4}>
                {user}
                {manager}
              </VStack>
            </Box>
          </Stack>
        ) : (
          <VStack spacing={4}>
            {operator}
            {user}
            {owner}
            {manager}
          </VStack>
        )}
      </DataStatus>
    </>
  );
}
